// ---------------------------------------------------------------------------
// Onboarding state: one fetch, shared by everything that needs to know who the
// user is and how far along their space is.
//
//     GET /api/v1/accounts/onboarding/state/
//     -> { persona, journey: {quick, advanced}, guides, readiness, auto_launch }
//
// Fetched once when the admin layout mounts and refetched when a guide ends or
// a page announces a milestone. Never polled: readiness is a handful of exists()
// reads, which is cheap once and expensive every four seconds.
//
// Division of labour with the backend, because it decides what this file does:
// the server owns the ORDER of a journey (it depends on persona and permission
// codes, which only the server can resolve) and this file owns AVAILABILITY (it
// knows which guides have actually shipped). So a journey id with no matching
// guide in the local registry is dropped silently here rather than rendered as
// a row that cannot start. That is the normal state between phases.
// ---------------------------------------------------------------------------

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import axios from '../utils/axios';
import useAuth from '../hooks/useAuth';
import { coach } from './coachBus';
import { COACH_EVENT } from './events';
import { GUIDES } from './guides';
import { isGuideDone, normalizeProgress } from './progress';

const ENDPOINT = '/api/v1/accounts/onboarding/state/';

const EMPTY = {
  loading: true,
  available: false,
  persona: null,
  personaLabel: '',
  autoLaunch: false,
  journey: { quick: [], advanced: [] },
  guides: {},
  readiness: null,
};

const OnboardingStateContext = createContext(null);

export function useOnboardingState() {
  return useContext(OnboardingStateContext) || { ...EMPTY, loading: false, refresh: () => {} };
}

// Milestones worth a refetch: each one can flip a readiness item from red to
// green, and the card is the first place the user looks for that.
const REFRESH_ON = [
  COACH_EVENT.campusProfileSaved,
  COACH_EVENT.workingHoursSaved,
  COACH_EVENT.spaceCreated,
  COACH_EVENT.seatsAdded,
  COACH_EVENT.planCreated,
  COACH_EVENT.billingCenterCreated,
  COACH_EVENT.taxConfigSaved,
  COACH_EVENT.invoiceNumberingSaved,
  COACH_EVENT.teamMemberInvited,
  COACH_EVENT.customRoleCreated,
  COACH_EVENT.memberCreated,
  COACH_EVENT.subscriptionCreated,
  COACH_EVENT.invoiceCreated,
  COACH_EVENT.meetingRoomCreated,
];

export function OnboardingStateProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(EMPTY);
  const inFlight = useRef(false);

  const isOrgUser = !!user && user.role === 'org';

  const refresh = useCallback(() => {
    if (!isOrgUser || inFlight.current) return;
    inFlight.current = true;
    axios
      .get(ENDPOINT)
      .then(({ data }) => {
        setState({
          loading: false,
          available: !!data?.persona,
          persona: data?.persona || null,
          personaLabel: data?.persona_label || '',
          autoLaunch: !!data?.auto_launch,
          journey: {
            quick: data?.journey?.quick || [],
            advanced: data?.journey?.advanced || [],
          },
          guides: data?.guides || {},
          readiness: data?.readiness || null,
        });
      })
      .catch(() => {
        // A failed state fetch must not take the dashboard with it. The card and
        // the launcher simply do not render, and every guide is still startable
        // by id from the help menu.
        setState({ ...EMPTY, loading: false });
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [isOrgUser]);

  useEffect(() => {
    if (!isOrgUser) {
      setState({ ...EMPTY, loading: false });
      return;
    }
    refresh();
  }, [isOrgUser, refresh]);

  // Refetch when a milestone lands. Debounced, because saving a space can emit
  // two of these in the same tick.
  useEffect(() => {
    if (!isOrgUser) return undefined;
    let timer = null;
    const queue = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(refresh, 1200);
    };
    const unsubscribes = REFRESH_ON.map((name) => coach.subscribe(name, queue));
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribes.forEach((off) => off());
    };
  }, [isOrgUser, refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);

  return (
    <OnboardingStateContext.Provider value={value}>{children}</OnboardingStateContext.Provider>
  );
}

OnboardingStateProvider.propTypes = { children: PropTypes.node };

/**
 * Journey ids turned into guides that actually exist, with their progress
 * attached. Ids the frontend has not shipped yet are dropped.
 *
 * @returns {Array<{guide: object, progress: object, done: boolean}>}
 */
export function resolveJourney(ids, guidesProgress) {
  return (ids || [])
    .map((id) => GUIDES[id])
    .filter(Boolean)
    .map((guide) => {
      const progress = normalizeProgress(guidesProgress?.[guide.id]);
      return { guide, progress, done: isGuideDone(progress) };
    });
}

/** The first guide in a journey that is not finished, or null if all are. */
export function nextInJourney(ids, guidesProgress) {
  return resolveJourney(ids, guidesProgress).find((entry) => !entry.done) || null;
}
